import React, { Suspense, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";
import {
  Award,
  Globe,
  Layers,
  Linkedin,
  Shield,
  TrendingDown,
  Zap,
} from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import {
  LEADERSHIP,
  TEAM,
  ENTITIES,
  RECOGNITION,
} from "@/data/people";
import { PARTNERS_CURATED } from "@/data/partners";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneG = React.lazy(() => import("@/components/scenes/SceneG"));

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

const DIFF_ICONS = [Layers, Zap, TrendingDown, Shield, Globe, Award];

function initials(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ name, src, size = 96 }) {
  if (src) {
    return (
      <img
        src={src}
        alt={`Portrait of ${name}`}
        loading="lazy"
        decoding="async"
        className="rounded-[14px] object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      aria-hidden
      className="flex items-center justify-center rounded-[14px] bg-surface-raised text-frac-white"
      style={{ width: size, height: size }}
    >
      <span
        className="font-bold tracking-tight"
        style={{ fontSize: Math.round(size * 0.36) }}
      >
        {initials(name)}
      </span>
    </div>
  );
}

function LeaderCard({ person, title, linkedinAria }) {
  const titleLines = title.split("/").map((s) => s.trim());
  return (
    <Card
      variant="glass"
      className="flex h-full flex-col"
      data-testid={`leader-${person.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start gap-5">
        <Avatar name={person.name} src={person.headshot} size={88} />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold leading-snug text-ink-primary">
            {person.name}
          </h3>
          <div className="mt-2 text-[13px] leading-snug text-ink-secondary">
            {titleLines.map((line, i) => (
              <span key={i} className="block">
                {line}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <a
          href={person.li}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={linkedinAria}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-hairline text-ink-secondary transition-colors duration-fast ease-out hover:border-active-border hover:text-frac-violet"
        >
          <Linkedin className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        </a>
      </div>
    </Card>
  );
}

function TeamCard({ person, title, linkedinAria }) {
  return (
    <div
      className="flex items-start gap-4 rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.55)] p-5 transition-colors duration-base ease-out hover:border-active-border"
      data-testid={`team-${person.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Avatar name={person.name} src={person.headshot} size={56} />
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-ink-primary">
          {person.name}
        </div>
        <div className="mt-1 text-[12px] leading-snug text-ink-secondary">
          {title}
        </div>
      </div>
      <a
        href={person.li}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={linkedinAria}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-tertiary transition-colors duration-fast ease-out hover:text-frac-violet"
      >
        <Linkedin className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
      </a>
    </div>
  );
}

function Hero() {
  const { t } = useT("company");
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="company-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Story() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const rawProgress = useMotionValueState(scrollYProgress, 0);
  const progress = Math.max(0, Math.min(1, (rawProgress - 0.10) / 0.50));
  const { t } = useT("company");

  return (
    <section
      ref={ref}
      className="container-frac py-16 md:py-24"
      data-testid="company-story"
    >
      <div className="mx-auto max-w-5xl">
        <div className="max-w-3xl">
          <Reveal>
            <span className="eyebrow">{t("story.eyebrow")}</span>
          </Reveal>
          <MaskReveal
            as="h2"
            className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
          >
            {t("story.h2Line1")}{" "}
            <span
              className="bg-gradient-brand bg-clip-text text-transparent"
              data-testid="company-story-gradient"
            >
              {t("story.h2Highlight")}
            </span>
            {t("story.h2Line2")}
          </MaskReveal>
          <Reveal delay={200}>
            <p className="mt-6 text-lg leading-normal text-ink-secondary">
              {t("story.body")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={300}>
          <div
            className="relative mt-12 w-full overflow-hidden rounded-[16px] border border-hairline"
            style={{ aspectRatio: "16 / 9", minHeight: 320 }}
          >
            <Suspense
              fallback={
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "var(--surface-base)",
                  }}
                />
              }
            >
              <SceneG
                scrollProgress={progress}
                style={{ position: "absolute", inset: 0 }}
              />
            </Suspense>
          </div>
        </Reveal>

        <Reveal delay={450}>
          <div className="mt-10 flex justify-center">
            <FracStoryCta />
          </div>
        </Reveal>

        <noscript>
          <div className="mt-10 flex justify-center">
            <a
              href="https://drive.google.com/file/d/1MFVLCqwWJRRbgWFtoMFIUjQrGPAz1Ii4/view"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-full bg-frac-violet px-6 py-3 text-base font-semibold text-frac-white"
            >
              See the Frac Story &rarr;
            </a>
          </div>
        </noscript>
      </div>
    </section>
  );
}

function FracStoryCta() {
  const { t } = useT("company");
  return (
    <Button
      as="a"
      href="https://drive.google.com/file/d/1MFVLCqwWJRRbgWFtoMFIUjQrGPAz1Ii4/view"
      target="_blank"
      rel="noopener noreferrer"
      variant="primary-on-dark"
      size="lg"
      aria-label={t("fracStoryCtaAria")}
      data-testid="company-frac-story-cta"
    >
      {t("fracStoryCta")}
    </Button>
  );
}

function MissionVision() {
  const { t } = useT("company");
  return (
    <section
      id="about"
      className="container-frac py-16 md:py-24"
      data-testid="company-mission-vision"
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Reveal>
          <Card variant="dark" className="h-full border-hairline">
            <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
              {t("missionVision.missionLabel")}
            </div>
            <p className="mt-4 text-xl font-semibold leading-snug text-ink-primary sm:text-2xl">
              {t("missionVision.missionBody")}
            </p>
          </Card>
        </Reveal>
        <Reveal delay={150}>
          <Card variant="dark" className="h-full border-hairline">
            <div className="text-[11px] font-semibold uppercase tracking-caps text-eyebrow">
              {t("missionVision.visionLabel")}
            </div>
            <p className="mt-4 text-xl font-semibold leading-snug text-ink-primary sm:text-2xl">
              {t("missionVision.visionBody")}
            </p>
          </Card>
        </Reveal>
      </div>
    </section>
  );
}

function Genesis() {
  const { t } = useT("company");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="company-genesis"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("genesis.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("genesis.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("genesis.p1")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("genesis.p2")}
          </p>
        </Reveal>
        <Reveal delay={550}>
          <p className="mt-12 text-2xl font-bold leading-snug text-ink-primary sm:text-3xl lg:text-4xl">
            {t("genesis.quote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FoundersMessage() {
  const { t } = useT("company");
  return (
    <section
      id="founders-message"
      className="container-frac py-16 md:py-24"
      data-testid="company-founders-message"
    >
      <Reveal>
        <div className="relative mx-auto max-w-6xl overflow-hidden rounded-2xl bg-surface-raised px-6 py-12 md:px-8 md:py-16">
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full opacity-40 blur-3xl"
            style={{ background: "var(--gradient-brand)" }}
          />

          <div className="relative grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12">
            <Reveal delay={100} className="md:col-span-5">
              <div
                className="overflow-hidden rounded-[16px] border border-hairline"
                data-testid="founders-photo"
              >
                <img
                  src="/team/co-founders.jpg"
                  alt={t("founders.photoAlt")}
                  loading="lazy"
                  decoding="async"
                  className="block h-full w-full object-cover"
                />
              </div>
            </Reveal>

            <div className="md:col-span-7 md:pl-2">
              <Reveal>
                <span className="eyebrow">{t("founders.eyebrow")}</span>
              </Reveal>
              <MaskReveal
                as="h2"
                className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
              >
                {t("founders.h2")}
              </MaskReveal>

              <div className="relative mt-8">
                <span
                  aria-hidden
                  className="absolute left-0 top-[-16px] hidden select-none font-light leading-none text-frac-violet md:-left-8 md:block"
                  style={{ fontSize: "61px", fontWeight: 300 }}
                >
                  &ldquo;
                </span>
                <Reveal delay={150}>
                  <p
                    className="text-[20px] font-normal leading-[1.6] text-ink-primary"
                    data-testid="founders-message-p1"
                  >
                    {t("founders.p1")}
                  </p>
                </Reveal>
                <Reveal delay={350}>
                  <p
                    className="mt-6 text-[20px] font-normal leading-[1.6] text-ink-primary"
                    data-testid="founders-message-p2"
                  >
                    {t("founders.p2")}
                  </p>
                </Reveal>
                <Reveal delay={500}>
                  <p
                    className="mt-8 text-sm font-semibold uppercase tracking-caps text-frac-violet"
                    data-testid="founders-attribution"
                  >
                    {t("founders.attribution")}
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Differentiators() {
  const { t } = useT("company");
  const items = t("differentiators.items", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="company-differentiators"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("differentiators.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("differentiators.h2")}
        </MaskReveal>
      </div>
      <Stagger
        className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        gap={70}
      >
        {items.map((d, i) => {
          const Icon = DIFF_ICONS[i];
          return (
            <StaggerItem key={i}>
              <Card
                variant="glass"
                className="h-full"
                data-testid={`differentiator-${i}`}
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-hairline bg-surface-elevated/60">
                  {Icon && <Icon className="h-4 w-4 text-frac-violet" aria-hidden />}
                </div>
                <h3 className="mt-4 text-base font-bold text-ink-primary">
                  {d.label}
                </h3>
                <p className="mt-2 text-sm leading-normal text-ink-secondary">
                  {d.body}
                </p>
              </Card>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}

function Leadership() {
  const { t } = useT("company");
  const titles = t("leadership.titles", { returnObjects: true }) || {};
  return (
    <section
      id="leadership"
      className="container-frac py-24 md:py-32"
      data-testid="company-leadership"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("leadership.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("leadership.h2")}
        </MaskReveal>
      </div>
      <Stagger
        className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3"
        gap={80}
      >
        {LEADERSHIP.map((p) => (
          <StaggerItem key={p.name}>
            <LeaderCard
              person={p}
              title={titles[p.name] || p.title}
              linkedinAria={t("leadership.linkedinAria", { name: p.name })}
            />
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Team() {
  const { t } = useT("company");
  const titles = t("leadership.titles", { returnObjects: true }) || {};
  return (
    <section
      id="team"
      className="container-frac py-24 md:py-32"
      data-testid="company-team"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("team.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("team.h2")}
        </MaskReveal>
      </div>
      <Stagger
        className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        gap={50}
      >
        {TEAM.map((p) => (
          <StaggerItem key={p.name}>
            <TeamCard
              person={p}
              title={titles[p.name] || p.title}
              linkedinAria={t("leadership.linkedinAria", { name: p.name })}
            />
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Entities() {
  const { t } = useT("company");
  const items = t("entities.items", { returnObjects: true }) || {};
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="company-entities"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("entities.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("entities.h2")}
        </MaskReveal>
      </div>
      <Stagger
        className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3"
        gap={70}
      >
        {ENTITIES.map((e) => (
          <StaggerItem key={e.name}>
            <Card
              variant="glass"
              className="h-full"
              data-testid={`entity-${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <h3 className="text-base font-bold text-ink-primary">
                {e.name}
              </h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {items[e.name] || e.location}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Recognition() {
  const { t } = useT("company");
  const items = t("recognition.items", { returnObjects: true }) || {};
  return (
    <section
      id="recognition"
      className="container-frac py-24 md:py-32"
      data-testid="company-recognition"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("recognition.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("recognition.h2")}
        </MaskReveal>
      </div>
      <Stagger
        className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        gap={50}
      >
        {RECOGNITION.map((r) => (
          <StaggerItem key={r.name}>
            <div
              className="relative h-full rounded-[16px] border border-hairline bg-[rgba(21,10,36,0.55)] p-6 transition-colors duration-base ease-out hover:border-active-border"
              data-testid={`recognition-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span
                aria-hidden
                className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full bg-frac-violet"
              />
              <div className="pl-3">
                <h3 className="text-[15px] font-bold leading-snug text-ink-primary">
                  {r.name}
                </h3>
                <p className="mt-2 text-[13px] leading-normal text-ink-secondary">
                  {items[r.name] || r.note}
                </p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function Partners() {
  const { t } = useT("company");
  return (
    <section
      id="partners"
      className="container-frac py-24 md:py-32"
      data-testid="company-partners"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("partners.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("partners.h2")}
        </MaskReveal>
      </div>

      <Stagger
        className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        gap={60}
        data-testid="company-partners-grid"
      >
        {PARTNERS_CURATED.map((p) => {
          const tileBase =
            "group relative flex h-full w-full items-center justify-center overflow-hidden rounded-[12px] border border-hairline bg-[rgba(237,241,245,0.03)] p-6";
          const linkableClasses =
            "transition-all duration-base ease-out hover:-translate-y-0.5 hover:border-active-border hover:bg-[rgba(114,0,219,0.06)]";
          const nonLinkableClasses =
            "transition-colors duration-base ease-out";
          const imgClasses =
            "max-h-12 w-auto max-w-full object-contain opacity-60 transition-all duration-base ease-out [filter:grayscale(1)_brightness(0.95)] group-hover:opacity-100 group-hover:[filter:none]";
          const tileStyle = { aspectRatio: "5 / 3" };
          const img = (
            <img
              src={p.logo}
              alt={t("partners.logoAlt", { name: p.name })}
              loading="lazy"
              decoding="async"
              className={imgClasses}
            />
          );

          if (p.url) {
            return (
              <StaggerItem key={p.slug}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("partners.linkedAria", { name: p.name })}
                  data-testid={`partner-${p.slug}`}
                  className={`${tileBase} ${linkableClasses}`}
                  style={tileStyle}
                >
                  {img}
                </a>
              </StaggerItem>
            );
          }

          return (
            <StaggerItem key={p.slug}>
              <div
                aria-label={t("partners.unlinkedAria", { name: p.name })}
                data-testid={`partner-${p.slug}`}
                className={`${tileBase} ${nonLinkableClasses}`}
                style={tileStyle}
              >
                {img}
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal delay={350}>
        <p className="mt-8 max-w-3xl text-xs italic text-ink-tertiary">
          {t("partners.note")}
        </p>
      </Reveal>
    </section>
  );
}

function Cta() {
  const { t } = useT("company");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="company-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("cta.h2")}
        </MaskReveal>
        <Reveal delay={250}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="company-cta-primary"
            >
              {t("cta.primary")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Company() {
  return (
    <>
      <PageSeo
        title="Company — About Frac"
        description="A real-world asset intelligence company from Singapore and Kuala Lumpur. Founded 2022. Eight signed enterprise deals. USD 400M+ in pre-tokenized asset value."
        path="/company"
        image="/og/company.png"
      />
      <div data-testid="page-company">
        <Hero />
        <Story />
        <MissionVision />
        <Genesis />
        <FoundersMessage />
        <Differentiators />
        <Leadership />
        <Team />
        <Entities />
        <Recognition />
        <Partners />
        <Cta />
      </div>
    </>
  );
}
